import { CurrencyName, PricesProps } from "../context/priceContext"

export enum priceDataGeneratorReference {
  packagePrice = "{priceData.price}",
  packageUpgradePrice = "{priceData.upgradePrice}",
  simPrice = "{priceData.simPrice}",
  simAdditionalPrice = "{priceData.simAdditionalPrice}",
  priceEsim = "{priceData.priceEsim}",
}

type PriceSourceKey = keyof typeof priceDataGeneratorReference

export const getLocaleMatchedCurrency = (location?: string, region?: string): CurrencyName => {
  // render in front-end
  if (region) {
    if (region === "Europe") {
      switch (location) {
        case "uk":
          return "GBP"
        case "id":
          return "USD"
        default:
          return "EUR"
      }
    } else {
      switch (location) {
        // check for Brazil
        case "pt":
          return "BRL"
        default:
          return "USD"
      }
    }
  } else {
    switch (location) {
      case "fr":
        return "EUR"
      case "de":
        return "EUR"
      case "pt":
        return "BRL"
      case "es":
        return "EUR"
      case "id":
        return "USD"
      case "nl":
        return "EUR"
      default:
        return "USD"
    }
  }
}

const isEUR = (currencyCode: string): boolean => (currencyCode === "EUR" ? true : false)

// Prices are presented as cents in Stripe
const formatStripePrice = (price: number, currencyCode: string): string =>
  isEUR(currencyCode) ? price.toFixed(2).replace(".", ",") : price.toFixed(2)

/**
 * Used to replace {priceData.?} in contentful strings.
 * Can be passed a single numeric `price` to replace `target` string,
 * or an array of each to replace e.g. `price`[0] for `target`[0]
 */
export const generatePriceWithCurrency = (
  currencyCode: string,
  price: number | number[],
  currencySymbol: string,
  content: string,
  target: string | string[]
): string => {
  if (Array.isArray(target) || Array.isArray(price)) {
    if (!Array.isArray(target) || !Array.isArray(price)) {
      throw new Error("Both target and price should be array if using array as an argument")
    } else if (target.length !== price.length) {
      throw new Error("Both target and price should have the same number of elements")
    } else {
      return target.reduce(
        (res, currentTarget, index) =>
          generatePriceWithCurrency(currencyCode, price[index], currencySymbol, res, currentTarget),
        content
      )
    }
  } else {
    const dynamicPrice: string = isEUR(currencyCode)
      ? `${formatStripePrice(price, currencyCode)} ${currencySymbol}`
      : `${currencySymbol} ${formatStripePrice(price, currencyCode)}`

    const originalDynamicPrice: string = isEUR(currencyCode)
      ? `${formatStripePrice(price * 2, currencyCode)} ${currencySymbol}`
      : `${currencySymbol} ${formatStripePrice(price * 2, currencyCode)}`

    const contentWithOriginalPrice = content.replace(
      "{priceData.originalSimPrice}",
      originalDynamicPrice
    )

    const res = content.includes("{priceData.originalSimPrice}")
      ? contentWithOriginalPrice.replace(new RegExp(target, "g"), dynamicPrice)
      : content.replace(new RegExp(target, "g"), dynamicPrice)

    return res
  }
}

/**
 * This is a proxy for generatePriceWithCurrency
 * method so we don't keep repeating the same arguments everytime for
 * every component
 */
export const generateAllPriceWithCurrency = (
  currencyCode: string,
  priceSource: PricesProps,
  currencySymbol: string,
  content: string
): string => {
  return generatePriceWithCurrency(
    currencyCode,
    Object.keys(priceDataGeneratorReference).map(key => priceSource[key as PriceSourceKey] || 0),
    currencySymbol,
    content,
    Object.values(priceDataGeneratorReference)
  )
}

export const formatPriceWithCurrency = (
  price: number,
  currencyCode: string,
  currencySymbol: string
): string => {
  const res = isEUR(currencyCode)
    ? `${formatStripePrice(price, currencyCode)} ${currencySymbol}`
    : `${currencySymbol} ${formatStripePrice(price, currencyCode)}`

  return res
}
